import { CommonModule } from "@angular/common";
import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    signal,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import SwiperCore, {
    A11y,
    FreeMode,
    Lazy,
    Mousewheel,
    Navigation,
    Pagination,
    Scrollbar,
    SwiperOptions,
} from "swiper";
import type Swiper from "swiper";
import { SwiperComponent, SwiperModule } from "swiper/angular";
import {
    A11yOptions,
    LazyOptions,
    MousewheelOptions,
    PaginationOptions,
    ScrollbarOptions,
} from "swiper/types";

import {
    IconButtonComponent,
    IconButtonSize,
    MEDIUM,
} from "@hermes/aphrodite/icon-button";
import {
    registerSvgs,
    svgArrowLeftBold,
    svgArrowRightBold,
    SvgIconComponent,
} from "@hermes/aphrodite/svg";
import {
    ControlPosition,
    LOW_POSITION,
    VideoComponent,
} from "@hermes/aphrodite/video";
import { Asset } from "@hermes/api-model-asset";
import { Context } from "@hermes/app-core";
import { Logger } from "@hermes/logger";
import { AssetMediaType } from "@hermes/utils-generic/constants";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic/directives/sr-only-no-snippet";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";
import { I18nAssetAttributeDirectiveModule } from "@hermes/utils-generic/i18n-asset-attribute";
import { LOGGER } from "@hermes/web-logger";

import { CarouselPartDirective } from "../../directives/carousel-part.directive";

export const PRIMARY_SLIDER = "primary";
export const HIGHLIGHTS_SLIDER = "highlight";
export const VARIANT_SLIDER = "variant";
export const GALLERY_SLIDER = "gallery";
export const TEXT_SLIDER = "text";
const TRANSITION_DELAY = 400;

export type CarouselAppearance =
    | typeof PRIMARY_SLIDER
    | typeof HIGHLIGHTS_SLIDER
    | typeof VARIANT_SLIDER
    | typeof GALLERY_SLIDER
    | typeof TEXT_SLIDER;

export interface BreakpointsConfiguration {
    [width: number]: SwiperOptions;
    [ratio: string]: SwiperOptions;
}

export interface ClickOnAssetEvent {
    carouselCurrentIndex: number | undefined;
    assetType: string | undefined;
}

export interface ControlsInset {
    right: string;
    left: string;
    top: string;
    bottom: string;
}

// install Swiper modules
SwiperCore.use([
    Pagination,
    Navigation,
    A11y,
    Lazy,
    Scrollbar,
    FreeMode,
    Mousewheel,
]);

export interface CarouselWrapperCustomCSS {
    display?: string;
    position?: string;
    height?: string;
    width?: string;
}

export type CarouselOptions = SwiperOptions;
export type CarouselScrollbarOptions = ScrollbarOptions;
export type CarouselA11yOptions = A11yOptions;
export type CarouselLazyOptions = LazyOptions;
export type CarouselPaginationOptions = PaginationOptions | boolean;
export type CarouselPaginationType =
    | "bullets"
    | "fraction"
    | "progressbar"
    | "custom";

const ID_CONTROL: string = "carousel-control";
const ID_PLAY_PAUSE: string = "play-pause-btn";

export const TOUCH = "touch";
export const MOUSE = "mouse";

export type InteractionType = typeof TOUCH | typeof MOUSE;

export const VERTICAL = "vertical";
export const HORIZONTAL = "horizontal";
export type CarouselDirection = typeof VERTICAL | typeof HORIZONTAL;

// We extends Swiper to add missing object properties in interface.
export interface SwiperCustom extends Swiper {
    slidesGrid: number[];
}
export class SwiperComponentCustom extends SwiperComponent {
    public declare swiperRef: SwiperCustom;
}

/**
 * This component implements the Hermès Carousel.
 * It follow the Aphrodite Design System guideline.
 *
 * Usage:
 * ```
 *  <h-carousel [input]="my-custom-config">
 *   <div *hSlide>My awesome slide</div>
 *  </h-carousel>
 * ```
 *
 */
@Component({
    selector: "h-carousel",
    templateUrl: "./carousel.component.html",
    styleUrls: ["./carousel.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // Need this, without them, slider does not be displayed correctly
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        SwiperModule,
        IconButtonComponent,
        VideoComponent,
        SrOnlyNoSnippetDirective,
        SvgIconComponent,
        I18nAssetAttributeDirectiveModule,
    ],
    standalone: true,
})
export class CarouselComponent
    implements OnInit, AfterContentInit, AfterViewInit
{
    /**
     * Carousel assets
     *
     * @default undefined
     */
    @Input()
    public assets?: Asset[];

    /**
     * Images alt
     *
     * @default []
     */
    @Input()
    public imageAlt: string = "";

    @Input()
    public appearance: CarouselAppearance = PRIMARY_SLIDER;

    @Input()
    public carouselWrapperCustomCSS: CarouselWrapperCustomCSS = {
        position: "relative",
        height: "inherit",
    };

    /**
     * Control the number of slides displayed by default
     *
     * @default 1
     */
    @Input()
    public slidesPerView: number | "auto" = 1;

    /**
     * Control the number of slides swiped by default
     *
     * @default 1
     */
    @Input()
    public slidesPerGroup: number = 1;

    /**
     * Control the index number of initial slide
     *
     * @default 0
     */
    @Input()
    public initialSlide: number = 0;

    /**
     * Control the direction of the carousel
     *
     * @default "horizontal"
     */
    @Input()
    public carouselDirection: CarouselDirection = "horizontal";

    @Input()
    public carouselScrollbarOptions: CarouselScrollbarOptions = {
        el: ".swiper-scrollbar",
        draggable: false,
    };

    /**
     * Control the distance between slides in px
     *
     * @default 0
     */
    @Input()
    public spaceBetween: number = 0;

    /**
     * Set to `true` to enable continuous loop mode
     *
     * @default false
     *
     * @note If you use it along with `slidesPerView: 'auto'` then you need to specify `loopedSlides`
     * parameter with amount of slides to loop (duplicate). Should not be used together with `rewind` mode
     */
    @Input()
    public isLoopEnabled = false;

    /**
     * Set to true to enable "rewind" mode. Loop should be disabled if we want to use rewind mode.
     *
     * When enabled:
     *
     * 1- Clicking "next" navigation button (or calling .slideNext()) when on last slide will slide back to the first slide.
     *
     * 2- Clicking "prev" navigation button (or calling .slidePrev()) when on first slide will slide forward to the last slide.
     *
     * @default false
     * */
    @Input()
    public isRewindEnabled = false;

    @Input()
    public a11yOptions: A11yOptions = { enabled: true };

    @Input()
    public lazyLoadingOptions: boolean | LazyOptions = {
        enabled: true,
        loadPrevNext: true,
        loadPrevNextAmount: 2,
    };

    /**
     * When enabled Swiper will force to load all images
     *
     * @default false
     */
    @Input()
    public preloadImages = false;

    @Input()
    public breakpointsConfiguration?: BreakpointsConfiguration;

    /**
     * If true, carousel will accept mouse events like touch events (click and drag to change slides)
     *
     * @default false
     */
    @Input()
    public isSimulateTouchEnabled = false;

    // Control the display of carousel pagination
    @Input()
    public carouselPaginationOptions: CarouselPaginationOptions = {
        dynamicBullets: true,
        type: "bullets",
        clickable: false,
    };

    // Control the mousewheel option of carousel
    @Input()
    public mousewheel: boolean | MousewheelOptions = false;

    /**
     * Control the focus on dots
     *
     * @default false
     */
    @Input()
    public isDotsFocusable = false;

    /**
     * Control the display of prev&next buttons
     *
     * @default true
     */
    @Input()
    public isControlsDisplayed = true;

    /**
     * Control the Background of prev&next buttons
     *
     * @default false
     */
    @Input()
    public controlsWithBackground: boolean = true;

    /**
     * Control the borderRadius of prev&next buttons
     *
     * @default true
     */
    @Input()
    public controlsBorderRadius: boolean = true;

    /**
     * Control the focus outline on the prev&next buttons
     *
     * @default false
     */
    @Input()
    public isKeyboardNavigationActive: boolean = true;

    /**
     * Control the opacity of prev&next buttons
     *
     * @default true
     */
    @Input()
    public controlsOpacity: boolean = true;

    /**
     * Control the height of arrows svgs
     *
     *
     * @default "10px"
     */
    @Input()
    public iconSize: number = 10;

    /** Set play/pause button position on video */
    @Input()
    public videoControlPosition: ControlPosition = LOW_POSITION;

    /** Set play/pause button size on video */
    @Input()
    public controlSize: IconButtonSize = MEDIUM;

    // Control the Position of prev&next buttons
    @Input()
    public controlsInset: ControlsInset = {
        right: "8px",
        left: "8px",
        top: "calc(50% - 15px)",
        bottom: "auto",
    };

    /**
     * Enable this feature to calculate each slides progress and visibility (slides in viewport will have additional visible class)
     * Fix an issue when right-clicking on a slide then nothing should happen
     *
     * @default true
     */
    @Input()
    public watchSlidesProgress = true;

    /**
     * Used to control carousel configuration
     *
     * @default false
     */
    @Input()
    public isMobile: boolean = false;

    /**
     * Wrap carousel assets inside button
     *
     * @default false
     */
    @Input()
    public wrapAssetInsideButton: boolean = false;

    @Input()
    public carouselTitle: string = "";

    /**
     * If `true`, then active slide will be centered.
     *
     * @default false
     */
    @Input()
    public centeredSlides: boolean = false;

    /**
     * If `true`, then all the slides of the carousel will be centered.
     *
     * @default false
     */
    @Input()
    public centerInsufficientSlides: boolean = false;

    /**
     * If true, then active slide will be centered without adding gaps at the
     * beginning and end of slider.Required centeredSlides: true.
     * Not intended to be used with loop or pagination
     *
     * @default false
     */
    @Input()
    public centeredSlidesBounds: boolean = false;

    /**
     * If `true` and click on any slide will produce transition to this slide
     *
     * @default false
     */
    @Input()
    public slideToClickedSlide: boolean = false;

    /**
     * To center content slides with flex justify content css
     */
    @Input()
    public flexCenterSlide: boolean = false;

    @Input()
    public imageFetchPriority: boolean = false;

    /**
     * Add (in px) additional slide offset in the end of the container (after all slides)
     */
    @Input()
    public slidesOffsetAfter = 0;

    /**
     * Prevents laggy transitions and subpixel issues when swiping.
     */
    @Input()
    public optimizeTransformAnimation = false;

    /**
     * SwiperJS component instance
     */
    @ViewChild("swiper")
    public swiper!: SwiperComponentCustom;

    @ViewChild("carousel")
    public carouselHtml!: ElementRef<HTMLDivElement>;

    // Need this, otherwise, slider is not displayed correctly
    @ContentChildren(CarouselPartDirective)
    public carouselParts!: QueryList<CarouselPartDirective>;

    /** Event sent when clicking on carousel next/prev buttons */
    @Output()
    public slideChanged: EventEmitter<number> = new EventEmitter<number>();

    @Output()
    public slideChangeTransitionEnd: EventEmitter<number> =
        new EventEmitter<number>();

    /** Event sent when clicking on carousel next/prev buttons */
    @Output()
    public clickOnAsset: EventEmitter<ClickOnAssetEvent> =
        new EventEmitter<ClickOnAssetEvent>();

    @Output()
    public slidersPerViewChangeByBreakpoint: EventEmitter<number> =
        new EventEmitter<number>();

    /** Event will be fired when Swiper reach last slide */
    @Output()
    public reachEnd: EventEmitter<number> = new EventEmitter<number>();

    /** Event will be fired when Swiper reach its beginning */
    @Output()
    public reachBeginning: EventEmitter<number> = new EventEmitter<number>();

    /** Event will be fired with first touch/drag move */
    @Output()
    public sliderFirstMove: EventEmitter<void> = new EventEmitter<void>();

    /** Event will be fired after transition. */
    @Output()
    public transitionEnd: EventEmitter<void> = new EventEmitter<void>();

    public assetMediaType = AssetMediaType;

    // Carousel configuration to initialize swiper with it
    public carouselConfiguration: CarouselOptions = {};

    // Indicates whether the video is in a playing in swiper
    public isPlayVideo: boolean = true;

    public slides!: Array<TemplateRef<unknown>>;

    // Handles focus events
    public isFocused: boolean = false;

    // Handles hover events
    public isHover: boolean = false;

    // Control Id
    public idControl: string = ID_CONTROL;

    // touch event type
    public lastInteractionType: InteractionType = MOUSE;

    // first slide is focused
    public displayRightControl: boolean = true;

    // last slide is focused
    public displayLeftControl: boolean = true;

    public isServerMode: boolean = false;

    public isSingleSlide = signal(false);

    public slidingDirection: WritableSignal<number | undefined> =
        signal(undefined);

    constructor(
        @Inject(LOGGER) private logger: Logger,
        private changeDetectorRef: ChangeDetectorRef,
        private renderer: Renderer2,
        private context: Context,
    ) {
        registerSvgs([svgArrowLeftBold, svgArrowRightBold]);
    }

    /**
     * allows display slide to particular index
     */
    @Input()
    public set slideTo(slideTo: number) {
        if (slideTo >= 0) {
            this.swiper?.swiperRef?.slideToLoop(slideTo, 0);
        }
    }

    /*
    / A11y
    / Use Angular's HostListener to differentiate between touch and mouse interactions (mobile & desktop with zoom +200%).
    /
    */
    @HostListener("touchstart")
    public onTouched(): void {
        this.lastInteractionType = TOUCH;
    }

    public ngOnInit() {
        // If only one asset is provided, we disable visual pagination and infinite loop.
        if (this.assets && this.assets.length < 2) {
            this.isControlsDisplayed = false;
            this.isLoopEnabled = false;
        }
        this.carouselConfiguration = {
            slidesPerView: this.slidesPerView,
            slidesPerGroup: this.slidesPerGroup,
            initialSlide: this.initialSlide,
            direction: this.carouselDirection,
            spaceBetween: this.spaceBetween,
            simulateTouch: this.isSimulateTouchEnabled,
            rewind: this.isRewindEnabled,
            breakpoints: this.breakpointsConfiguration,
            pagination: this.carouselPaginationOptions,
            scrollbar: this.carouselScrollbarOptions,
            mousewheel: this.mousewheel,
            a11y: this.a11yOptions,
            loop: this.isLoopEnabled && this.context.isInBrowserMode(),
            lazy: this.lazyLoadingOptions,
            watchSlidesProgress: this.watchSlidesProgress,
            preloadImages: this.preloadImages,
            centerInsufficientSlides: this.centerInsufficientSlides,
            slidesOffsetAfter: this.slidesOffsetAfter,
        };
        if (this.appearance === VARIANT_SLIDER) {
            this.carouselConfiguration = {
                ...this.carouselConfiguration,
                centeredSlides: this.centeredSlides,
                centeredSlidesBounds: this.centeredSlidesBounds,
                slideToClickedSlide: this.slideToClickedSlide,
            };
        }
        if (
            this.isControlsDisplayed &&
            !this.isRewindEnabled &&
            !this.isLoopEnabled
        ) {
            this.displayOnlyRightControl();
        }
        this.isServerMode = this.context.isInServerMode();

        this.isSingleSlide.set(this.slidesPerView === 1);

        this.changeDetectorRef.markForCheck();
    }

    public ngAfterContentInit(): void {
        this.updateSlides();
    }

    public ngAfterViewInit(): void {
        this.makeDotsInaccessible();
    }

    public trackByAsset(index: number): string {
        return `asset_${index}`;
    }

    public updateSlides(): void {
        const partsReference = this.carouselParts.toArray();
        this.slides = partsReference.map(
            (directive) => directive.templateReference,
        );

        if (!this.slides) {
            this.logger.warn(
                "Bad usage of <h-carousel> component, did you set *slide ?",
            );
        }
        this.changeDetectorRef.markForCheck();
    }

    public slidingTo(direction?: number) {
        if (this.isSingleSlide()) {
            this.slidingDirection.set(direction);
        }
    }

    public onSlideChange(): void {
        if (
            !this.isControlsDisplayed &&
            (!this.isMobile || !this.isFocused) &&
            this.swiper?.swiperRef
        ) {
            this.slideChanged.emit(this.swiper?.swiperRef.realIndex);
        }

        this.makeDotsInaccessible();
        this.updateSlides();
        this.changeDetectorRef.detectChanges();
    }

    public onBreakpointChange(swiperOptions: SwiperOptions) {
        // In SwiperOptions, slidesPerView is a property so parse to object to read the value
        const newSwiperOptions = new Object(swiperOptions) as {
            slidesPerView: number;
        };
        this.slidersPerViewChangeByBreakpoint.emit(
            newSwiperOptions.slidesPerView,
        );
        this.isSingleSlide.set(newSwiperOptions.slidesPerView === 1);
    }

    public makeDotsInaccessible(): void {
        if (!this.isDotsFocusable) {
            // disable focus on dots
            this.carouselHtml.nativeElement
                .querySelectorAll(".swiper-pagination-bullet")
                .forEach((span, _index) => {
                    span.setAttribute("tabindex", "-1");
                    span.setAttribute("aria-hidden", "true");
                });
        }
    }

    public slideNext(event: Event): void {
        this.displayLeftControl = true;
        this.swiper.swiperRef.slideNext(TRANSITION_DELAY);
        this.slideChanged.emit(this.swiper?.swiperRef.realIndex);
        stopEventPropagation(event);
    }

    public slidePrev(event: Event): void {
        this.displayRightControl = true;
        this.swiper.swiperRef.slidePrev(TRANSITION_DELAY);
        this.slideChanged.emit(this.swiper?.swiperRef.realIndex);
        stopEventPropagation(event);
    }

    /**
     * Updates the playback state of the video in swiper.
     * @param isPlay
     */
    public onPlayPauseVideo(isPlay: boolean): void {
        this.isPlayVideo = isPlay;
    }

    public onImageReady(): void {
        if (this.appearance === PRIMARY_SLIDER) {
            const dots =
                this.carouselHtml.nativeElement.querySelectorAll(
                    ".swiper-pagination",
                )[0];
            this.renderer.setStyle(dots, "opacity", 1);
        }
    }

    public onSlideChangeTransitionEnd(): void {
        this.slideChangeTransitionEnd.emit(this.swiper?.swiperRef?.realIndex);
        this.updateSlides();
        this.changeDetectorRef.detectChanges();
    }

    public onReachEnd() {
        this.displayOnlyLeftControl();
        this.reachEnd.emit();
    }

    public onReachBeginning() {
        this.displayOnlyRightControl();
        this.reachBeginning.emit();
    }

    /**
     * Handles focus events within the parent component.
     * For mobile accessibility (+200% zoom), where visual feedback must be clear and immediate to assist users in navigating content.
     */

    public onFocusIn() {
        if (
            this.isMobile &&
            !this.isControlsDisplayed &&
            this.assets &&
            this.lastInteractionType === MOUSE
        ) {
            this.isFocused = true;
        }
    }

    /**
     * Handles focus out events from the parent or right-control component specifically for mobile mode (+200% zoom).
     * @param {FocusEvent} event -
     * @param {string} ID_CONTROL -
     */

    public onFocusOut(event: FocusEvent): void {
        if (this.isMobile && !this.isControlsDisplayed && this.assets) {
            const relatedTarget = event.relatedTarget as HTMLElement;
            this.isFocused = Boolean(
                relatedTarget &&
                    (relatedTarget.id === ID_CONTROL ||
                        relatedTarget.id === ID_PLAY_PAUSE),
            );
        }
    }

    public onMouseleave() {
        if (this.isMobile && !this.isControlsDisplayed && this.assets) {
            this.isFocused = false;
        }
    }

    public displayOnlyRightControl() {
        this.displayRightControl = true;
        this.displayLeftControl = false;
    }

    public displayOnlyLeftControl() {
        this.displayLeftControl = true;
        this.displayRightControl = false;
    }
}
